/**
 * getDuplicateEntryKeys
 * ----------------------
 *
 * Get the keys of entries in an object that have duplicate values at a specified path.
 *
 * @template ObjectValue - The type of the values in the object.
 * @param {Object.<string, ObjectValue>} object - The object to search for duplicate values.
 * @param {string} path - The path to the value within each object entry to check for duplicates.
 * @param transformValue{(value: any) => string} - A function to transform the value at the specified path before checking for duplicates.
 * @returns {string[]} - An array of keys that have duplicate values at the specified path.
 */
export const getDuplicateEntryKeysUtil = <ObjectValue = any>(
	object: { [key: string]: ObjectValue },
	path: string,
	transformValue?: (value: any) => string
): string[] => {
	// Denis: If the object is not an object or is null, return an empty array.
	if (typeof object !== 'object' || object === null) {
		return [];
	}

	// Denis: If the path is not a string or is an empty string, return an empty array.
	if (typeof path !== 'string' || path.trim() === '') {
		return [];
	}

	// Denis: Get all values at the specified path and create a map of values to keys.
	const valueMap = Object.entries(object).reduce((acc, [key, value]: [string, ObjectValue]) => {
		// Denis: If the path does not exist in the current value, do not add it to the value map.
		if (!value || !value[path]) {
			return acc;
		}

		// Denis: The value should be transformed to a string to use as a key in the value map.
		// A custom transform fucntion can be provided to transform the value before using it as a key.
		const currentValue =
			typeof transformValue === 'function'
				? transformValue(!!value && value[path])
				: String(!!value && value[path]);

		// Denis: If the current value is not in the accumulator, add it with the current key.
		if (currentValue && !acc[currentValue]) {
			return {
				...acc,
				[currentValue]: [key]
			};
		}

		// Denis: If the current value is in the accumulator, add the current key to the existing value array.
		if (currentValue && acc[currentValue]) {
			return {
				...acc,
				[currentValue]: [...acc[currentValue], key]
			};
		}

		return acc;
	}, {});

	// Denis: Get all duplicate invite keys in the value map in a flat array.
	return Object.values(valueMap).flatMap((keys: string[]) => (keys.length > 1 ? keys : []));
};
